import { useEffect, useId, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCommunityFavorite } from './communityFavorites';
import {
  setMobileBrowseView,
  useCommunitiesBrowse,
  useSelectCommunity,
} from './communitiesBrowseStore';
import {
  fetchPropertyDetail,
  formatPhoneCompact,
  getBedroomPriceLines,
  getCommunityAmenityFlags,
  getCommunityPath,
  getFullAddress,
  phoneHref,
  type CommunityAmenityFlag,
  type CommunityProperty,
} from './propertyUtils';

interface CommunityCardProps {
  property: CommunityProperty;
  /** Compact layout for map popups; list is a horizontal browse row */
  variant?: 'grid' | 'list' | 'popup';
  /** Sheet is a short docked preview; window is the desktop map popup */
  presentation?: 'window' | 'sheet';
  /** Narrower photo on list rows for side-by-side map layouts */
  compactMedia?: boolean;
  onClose?: () => void;
  className?: string;
}

interface GalleryImage {
  url: string;
  alt: string;
}

const AMENITY_META: Record<
  Exclude<CommunityAmenityFlag, 'specials' | 'available'>,
  { label: string; icon: string }
> = {
  pet: { label: 'Pet friendly', icon: 'M4 12c0-2 1.5-4 4-4s4 2 4 4-1.5 4-4 4-4-2-4-4zm8 0c0-2 1.5-4 4-4s4 2 4 4-1.5 4-4 4-4-2-4-4z' },
  pool: { label: 'Pool', icon: 'M4 14c2 1 4 1 6 0s4-1 6 0 4 1 6 0M4 18c2 1 4 1 6 0s4-1 6 0 4 1 6 0M8 8c1.5 0 2.5 1 4 1s2.5-1 4-1' },
  fitness: { label: 'Fitness', icon: 'M6 9v6M18 9v6M9 6h6v12H9zM4 11h2v2H4zm14 0h2v2h-2z' },
  laundry: { label: 'In-home laundry', icon: 'M5 5h14v14H5zM9 9h6M8 13h8M8 16h5' },
  parking: { label: 'Covered parking', icon: 'M4 18V8l8-4 8 4v10M8 18v-5h8v5' },
};

function PinIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z" />
    </svg>
  );
}

function HeartIcon({ filled }: { filled?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 21s-6.7-4.35-9.33-7.6C.7 10.9 1.2 7.4 3.9 5.85 5.7 4.8 8 5.05 9.5 6.5L12 9l2.5-2.5c1.5-1.45 3.8-1.7 5.6-.65 2.7 1.55 3.2 5.05 1.23 7.55C18.7 16.65 12 21 12 21z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.27 1.2.4 2.5.62 3.8.62.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.3 21 3 13.7 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.22 2.6.62 3.8.1.4 0 .8-.27 1.1L6.6 10.8z" />
    </svg>
  );
}

function GalleryIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="10" r="1.5" fill="currentColor" stroke="none" />
      <path d="M3 16l5-4 4 3 3-2 6 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AmenityIcon({ flag }: { flag: keyof typeof AMENITY_META }) {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d={AMENITY_META[flag].icon} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function stop(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

function extractGalleryImages(detail: CommunityProperty | null, fallback: CommunityProperty): GalleryImage[] {
  const gallery = Array.isArray(detail?.photo_gallery) ? detail!.photo_gallery : [];
  const fromGallery = gallery
    .map((item: { image?: string; alt_text?: string; title?: string; image_media?: { alt_text?: string } }) => {
      const url = String(item?.image || '').trim();
      if (!url) return null;
      return {
        url,
        alt: String(item?.alt_text || item?.image_media?.alt_text || item?.title || fallback.property_name || 'Community photo'),
      };
    })
    .filter(Boolean) as GalleryImage[];

  if (fromGallery.length > 0) return fromGallery;

  if (fallback.property_featured_image) {
    return [
      {
        url: String(fallback.property_featured_image),
        alt: String(fallback.property_name || 'Community photo'),
      },
    ];
  }
  return [];
}

function CommunityGalleryModal({
  open,
  title,
  images,
  loading,
  onClose,
}: {
  open: boolean;
  title: string;
  images: GalleryImage[];
  loading: boolean;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    setIndex(0);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') setIndex((prev) => (images.length ? (prev + 1) % images.length : 0));
      if (event.key === 'ArrowLeft') {
        setIndex((prev) => (images.length ? (prev - 1 + images.length) % images.length : 0));
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, images.length, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const current = images[index];

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgb(var(--ala-primary))]/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[rgb(var(--ala-border))] px-4 py-3">
          <h3 id={titleId} className="truncate font-heading text-base font-semibold text-[rgb(var(--ala-text))]">
            {title}
          </h3>
          <button
            type="button"
            aria-label="Close gallery"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-[rgb(var(--ala-text-muted))] transition hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="relative aspect-[16/10] bg-[rgb(var(--ala-surface))]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center font-body text-sm text-[rgb(var(--ala-text-muted))]">
              Loading gallery…
            </div>
          ) : current ? (
            <VibesitesImage
              url={current.url}
              alt={current.alt}
              sizes="(min-width: 900px) 896px, 100vw"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center font-body text-sm text-[rgb(var(--ala-text-muted))]">
              No photos available.
            </div>
          )}

          {images.length > 1 ? (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={() => setIndex((prev) => (prev - 1 + images.length) % images.length)}
                className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[rgb(var(--ala-background))]/95 text-[rgb(var(--ala-text))] shadow-sm"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M15 5l-7 7 7 7" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={() => setIndex((prev) => (prev + 1) % images.length)}
                className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-[rgb(var(--ala-background))]/95 text-[rgb(var(--ala-text))] shadow-sm"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <p className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-md bg-[rgb(var(--ala-primary))]/80 px-2.5 py-1 font-body text-xs text-[rgb(var(--ala-on-primary))]">
                {index + 1} / {images.length}
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function CommunityCard({
  property,
  variant = 'grid',
  presentation = 'window',
  compactMedia = false,
  onClose,
  className = '',
}: CommunityCardProps) {
  const { favorited, toggle: toggleFavorite } = useCommunityFavorite(property.multihub_property_id);
  const browse = useCommunitiesBrowse();
  const selectCommunity = useSelectCommunity();
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const propertyId = String(property.multihub_property_id || '');
  const selected = Boolean(propertyId && browse.selectedId === propertyId);
  const href = getCommunityPath(property);
  const website = property.property_website_url || '';
  const phone = formatPhoneCompact(property.property_phone);
  const tel = phoneHref(property.property_phone);
  const address = getFullAddress(property);
  const priceLines = getBedroomPriceLines(property);
  const flags = getCommunityAmenityFlags(property).filter(
    (flag): flag is keyof typeof AMENITY_META => flag !== 'specials' && flag !== 'available',
  );
  const iconFlags = flags.slice(0, 4);
  const isPopup = variant === 'popup';
  const isList = variant === 'list';
  const slug = String(property._slug || property.property_slug || '');

  const focusOnMap = (event: MouseEvent) => {
    stop(event);
    if (!propertyId) return;
    selectCommunity(propertyId, { focus: true });
    setMobileBrowseView('map');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const map = document.getElementById('communities-map');
        if (!map) return;
        const rect = map.getBoundingClientRect();
        if (rect.height > 0 && rect.top >= 0 && rect.bottom <= window.innerHeight + 1) return;
        map.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  };

  const openGallery = async (event: MouseEvent) => {
    stop(event);
    setGalleryOpen(true);
    setGalleryLoading(true);
    try {
      const detail = slug ? await fetchPropertyDetail(slug, propertyId || undefined) : null;
      setGalleryImages(extractGalleryImages(detail, property));
    } catch {
      setGalleryImages(extractGalleryImages(null, property));
    } finally {
      setGalleryLoading(false);
    }
  };

  const body = (
    <>
      <h3 className="font-heading text-lg font-bold tracking-tight text-[rgb(var(--ala-text))] md:text-xl">
        {property.property_name}
      </h3>

      {address ? (
        <p className="mt-1.5 flex items-start gap-1.5 font-body text-sm text-[rgb(var(--ala-text-muted))]">
          <PinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[rgb(var(--ala-secondary))]" />
          <span>{address}</span>
        </p>
      ) : null}

      {priceLines.length > 0 ? (
        <ul className="mt-3 space-y-1 font-body text-sm text-[rgb(var(--ala-text))]/85">
          {priceLines.map((line) => (
            <li key={line.label} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
              <span>{line.label}</span>
              <span className="font-heading text-sm font-semibold text-[rgb(var(--ala-secondary))]">
                {line.price}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {iconFlags.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-2">
          {iconFlags.map((flag) => (
            <li
              key={flag}
              title={AMENITY_META[flag].label}
              className="inline-flex items-center gap-1 rounded-md bg-[rgb(var(--ala-surface))] px-2 py-1 font-body text-[11px] text-[rgb(var(--ala-text-muted))]"
            >
              <AmenityIcon flag={flag} />
              <span>{AMENITY_META[flag].label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );

  const iconActions = !isPopup ? (
    <div className={`flex flex-col items-start gap-3 ${isList ? 'mt-5' : 'mt-auto pt-5'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={href}
          className="inline-flex items-center justify-center rounded-md bg-[rgb(var(--ala-primary))] px-3 py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition hover:opacity-90"
        >
          View Details
        </a>
        {website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            className="inline-flex items-center justify-center rounded-md border border-[rgb(var(--ala-text))]/25 px-3 py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition hover:border-[rgb(var(--ala-text))] hover:bg-[rgb(var(--ala-text))] hover:text-[rgb(var(--ala-on-primary))]"
          >
            Website
          </a>
        ) : null}
      </div>

      <div className="flex items-center justify-start gap-1.5">
        {phone && tel ? (
          <div className="relative">
            <button
              type="button"
              aria-expanded={phoneOpen}
              aria-label={phoneOpen ? 'Hide phone number' : 'Show phone number'}
              onClick={(event) => {
                stop(event);
                setPhoneOpen((open) => !open);
              }}
              className={`inline-flex h-11 w-11 items-center justify-center rounded-md transition ${
                phoneOpen
                  ? 'bg-[rgb(var(--ala-accent))] text-[rgb(var(--ala-on-primary))]'
                  : 'bg-[rgb(var(--ala-surface))] text-[rgb(var(--ala-text-muted))] hover:text-[rgb(var(--ala-text))]'
              }`}
            >
              <PhoneIcon />
            </button>
            {phoneOpen ? (
              <a
                href={tel}
                onClick={stop}
                className="absolute bottom-full left-0 mb-2 whitespace-nowrap rounded-md bg-[rgb(var(--ala-primary))] px-3 py-2 font-heading text-xs font-semibold text-[rgb(var(--ala-on-primary))] shadow-lg"
              >
                {phone}
              </a>
            ) : null}
          </div>
        ) : null}

        <button
          type="button"
          aria-label="View photo gallery"
          onClick={openGallery}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[rgb(var(--ala-surface))] text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))]"
        >
          <GalleryIcon />
        </button>

        <button
          type="button"
          onClick={focusOnMap}
          aria-label="View on map"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[rgb(var(--ala-surface))] text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))]"
        >
          <PinIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  ) : (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <a
        href={href}
        className="inline-flex items-center justify-center rounded-md bg-[rgb(var(--ala-primary))] px-3 py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition hover:opacity-90"
      >
        View community
      </a>
      {phone && tel ? (
        <a
          href={tel}
          onClick={stop}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-[rgb(var(--ala-text))]/25 px-3 py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition hover:border-[rgb(var(--ala-text))]"
        >
          <PhoneIcon />
          {phone}
        </a>
      ) : null}
      <button
        type="button"
        aria-label="View photo gallery"
        onClick={openGallery}
        className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[rgb(var(--ala-surface))] text-[rgb(var(--ala-text-muted))] transition hover:text-[rgb(var(--ala-text))]"
      >
        <GalleryIcon />
      </button>
    </div>
  );

  const mediaBadges = (
    <>
      {!isPopup ? (
        <button
          type="button"
          aria-label={favorited ? 'Remove from favorites' : 'Save community'}
          aria-pressed={favorited}
          onClick={(event) => {
            stop(event);
            toggleFavorite();
          }}
          className="absolute left-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[rgb(var(--ala-primary))]/90 text-[rgb(var(--ala-on-primary))] transition hover:bg-[rgb(var(--ala-primary))]"
        >
          <HeartIcon filled={favorited} />
        </button>
      ) : null}

      {isPopup && onClose ? (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgb(var(--ala-background))]/95 text-[rgb(var(--ala-text-muted))] shadow-sm transition hover:text-[rgb(var(--ala-text))]"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      ) : null}
    </>
  );

  const galleryModal = (
    <CommunityGalleryModal
      open={galleryOpen}
      title={property.property_name || 'Community gallery'}
      images={galleryImages}
      loading={galleryLoading}
      onClose={() => setGalleryOpen(false)}
    />
  );

  if (isPopup && presentation === 'sheet') {
    return (
      <>
        <article
          data-community-id={propertyId}
          className={`relative flex w-full overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-[0_12px_40px_rgb(var(--ala-primary)/0.2)] ${className}`}
        >
          <div className="relative min-h-28 w-28 shrink-0 self-stretch bg-[rgb(var(--ala-surface))] sm:w-36">
            {property.property_featured_image ? (
              <VibesitesImage
                url={property.property_featured_image}
                alt={property.property_name || ''}
                sizes="144px"
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : null}
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 py-3 pl-3 pr-12">
            <h3 className="truncate font-heading text-base font-bold tracking-tight text-[rgb(var(--ala-text))]">
              {property.property_name}
            </h3>
            {address ? (
              <p className="line-clamp-2 font-body text-xs text-[rgb(var(--ala-text-muted))]">{address}</p>
            ) : null}
            <a
              href={href}
              className="inline-flex w-fit items-center justify-center rounded-md bg-[rgb(var(--ala-primary))] px-3 py-2 font-heading text-xs font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-primary))] transition hover:opacity-90"
            >
              View community
            </a>
          </div>
          {onClose ? (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-2 top-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-[rgb(var(--ala-text-muted))] transition hover:bg-[rgb(var(--ala-surface))] hover:text-[rgb(var(--ala-text))]"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          ) : null}
        </article>
        {galleryModal}
      </>
    );
  }

  if (isList) {
    return (
      <>
        <article
          data-community-id={propertyId}
          className={`group overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-[0_8px_28px_rgb(var(--ala-primary)/0.12)] transition ring-offset-2 sm:flex ${
            selected ? 'ring-2 ring-[rgb(var(--ala-secondary))]' : ''
          } ${className}`}
          onMouseEnter={() => propertyId && selectCommunity(propertyId)}
        >
          <div
            className={`relative aspect-[16/10] shrink-0 overflow-hidden bg-[rgb(var(--ala-surface))] sm:aspect-auto sm:self-stretch ${
              compactMedia ? 'sm:w-[220px] 2xl:w-[260px]' : 'sm:w-[280px] lg:w-[320px]'
            }`}
          >
            {property.property_featured_image ? (
              <VibesitesImage
                url={property.property_featured_image}
                alt={property.property_name || ''}
                sizes={compactMedia ? '(min-width: 640px) 260px, 100vw' : '(min-width: 640px) 320px, 100vw'}
                className="h-full w-full object-cover sm:absolute sm:inset-0"
              />
            ) : null}
            {mediaBadges}
          </div>
          <div className="flex flex-1 flex-col justify-center p-5 md:p-6">
            {body}
            {iconActions}
          </div>
        </article>
        {galleryModal}
      </>
    );
  }

  return (
    <>
      <article
        data-community-id={propertyId}
        className={`group overflow-hidden rounded-xl bg-[rgb(var(--ala-background))] shadow-[0_8px_28px_rgb(var(--ala-primary)/0.12)] transition ring-offset-2 ${
          isPopup ? 'w-[min(100%,320px)]' : 'flex h-full flex-col'
        } ${selected && !isPopup ? 'ring-2 ring-[rgb(var(--ala-secondary))]' : ''} ${className}`}
        onMouseEnter={() => !isPopup && propertyId && selectCommunity(propertyId)}
      >
        <div
          className={`relative shrink-0 overflow-hidden bg-[rgb(var(--ala-surface))] ${
            isPopup || compactMedia ? 'aspect-[16/10]' : 'aspect-[3/2]'
          }`}
        >
          {property.property_featured_image ? (
            <VibesitesImage
              url={property.property_featured_image}
              alt={property.property_name || ''}
              sizes={isPopup ? '320px' : '(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'}
              className="h-full w-full object-cover"
            />
          ) : null}
          {mediaBadges}
        </div>
        <div className={isPopup ? 'p-4' : compactMedia ? 'flex flex-1 flex-col p-4' : 'flex flex-1 flex-col p-4 md:p-5'}>
          {body}
          {iconActions}
        </div>
      </article>
      {galleryModal}
    </>
  );
}

export default CommunityCard;
